import { useMemo, useState } from "react";
import { resolveReviewStatus } from "@hooks/useJobResults";
import type { UseJobResultsState } from "@hooks/useJobResults.types";
import {
  MappingStatus,
  ReviewStatus,
} from "@models/job";
import {
  ConfidenceBand,
  MappingResultKind,
} from "@models/mappingResult";
import type { MappingResultRow } from "@models/mappingResult";
import type { ReviewStatusFilter } from "@utils/jobResultsHelpers";

export interface UseResultsFilterResult {
  statusFilter: ReviewStatusFilter;
  setStatusFilter: (filter: ReviewStatusFilter) => void;
  priorityOnly: boolean;
  setPriorityOnly: (value: boolean) => void;
  /** Show only `no_match` rows, including TM missing values and WD items. */
  missingOnly: boolean;
  setMissingOnly: (value: boolean) => void;
  statusCounts: Record<ReviewStatusFilter, number>;
  priorityCount: number;
  missingCount: number;
  filteredRows: MappingResultRow[];
}

/**
 * Filter the loaded results by review state or machine findings.
 * All counts cover rows loaded so far, not the full paginated job.
 */
export const useResultsFilter = (
  results: UseJobResultsState,
): UseResultsFilterResult => {
  const [statusFilter, setStatusFilter] = useState<ReviewStatusFilter>("all");
  const [priorityOnly, setPriorityOnlyState] = useState(false);
  const [missingOnly, setMissingOnlyState] = useState(false);

  // The two toggles exclude each other rather than intersect: a catalogue miss
  // deliberately carries NO confidence band (the miss IS the warning, so the
  // backend leaves the band off to keep one signal per row), which makes their
  // intersection always empty. Letting both be on would hand the reviewer an
  // empty table and no hint why.
  const setPriorityOnly = (value: boolean): void => {
    setPriorityOnlyState(value);
    if (value) setMissingOnlyState(false);
  };
  const setMissingOnly = (value: boolean): void => {
    setMissingOnlyState(value);
    if (value) setPriorityOnlyState(false);
  };

  const statusCounts = useMemo(() => {
    const counts: Record<ReviewStatusFilter, number> = {
      all: results.rows.length,
      [ReviewStatus.Pending]: 0,
      [ReviewStatus.Accepted]: 0,
      [ReviewStatus.Rejected]: 0,
      [ReviewStatus.Corrected]: 0,
      [ReviewStatus.Failed]: 0,
    };
    for (const row of results.rows) {
      const effective = resolveReviewStatus(
        row,
        results.edits.get(row.rowIndex),
      );
      counts[effective] += 1;
    }
    return counts;
  }, [results.rows, results.edits]);

  // WD supplies a separate confidence band; the shared `matched` verdict
  // cannot identify low-confidence TM rows, so do not mark all matches urgent.
  const isPriority = (row: MappingResultRow) =>
    row.kind === MappingResultKind.Oe &&
    row.target?.confidenceBand === ConfidenceBand.Low;

  const priorityCount = useMemo(
    () => results.rows.filter(isPriority).length,
    [results.rows],
  );

  // The machine verdict is fixed after mapping. A correction may change the
  // effective target, but a no_match result remains visible in this filter.
  const isMissing = (row: MappingResultRow) =>
    row.mappingStatus === MappingStatus.NoMatch;

  const missingCount = useMemo(
    () => results.rows.filter(isMissing).length,
    [results.rows],
  );

  const filteredRows = useMemo(() => {
    return results.rows.filter((row) => {
      if (priorityOnly && !isPriority(row)) return false;
      if (missingOnly && !isMissing(row)) return false;
      if (
        statusFilter !== "all" &&
        resolveReviewStatus(row, results.edits.get(row.rowIndex)) !==
          statusFilter
      ) {
        return false;
      }
      return true;
    });
  }, [results.rows, results.edits, statusFilter, priorityOnly, missingOnly]);

  return {
    statusFilter,
    setStatusFilter,
    priorityOnly,
    setPriorityOnly,
    missingOnly,
    setMissingOnly,
    statusCounts,
    priorityCount,
    missingCount,
    filteredRows,
  };
};
